import type { FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { Input } from '@/lib/base/forms/input/input';

import { ApiAccessGrantPicker } from './api-access-grant-picker';
import type {
  GrantSelection,
  MintCredentialParams,
} from './api-access-service';
import { formatGrantBuildError } from './api-access-service';
import { useGrantScopings } from './use-grant-scopings';

/** Pre-filled so the slideout opens ready to submit. */
const DEFAULT_CREDENTIAL_NAME = 'Service account';

export interface ApiAccessCreateFormProps {
  orgId: number;
  /**
   * DOM id of the rendered `<form>`. The submit button lives in the slideout's
   * pinned footer (outside this form's subtree) and targets it via `form=`,
   * so a long type list never scrolls the primary action out of view.
   */
  formId: string;
  onSubmit: (params: MintCredentialParams) => void;
  isSubmitting: boolean;
  /** Reports whether a submit would currently succeed, so the footer button can enable/disable. */
  onCanSubmitChange?: (canSubmit: boolean) => void;
  /** Reports name-only dirtiness up to the always-mounted slideout wrapper. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Reports every name change up to the always-mounted wrapper, so it can
   * drop a retry-reuse entity id from a prior failed mint attempt — a stale
   * id must never be reused once the user is naming a different credential.
   */
  onNameChange?: (name: string) => void;
  /**
   * Reports every grant-selection change up to the always-mounted wrapper, so
   * it can drop a retry-reuse entity id from a prior failed mint attempt — a
   * retry (which passes `existingPrincipalEntityId`) must never silently mint
   * with a STALE selection.
   */
  onGrantsChange?: (grants: GrantSelection[]) => void;
}

/**
 * The create form body. Extracted so it mounts only while the slideout is open
 * (the parent renders it behind `isOpen`). Keeping `useGrantScopings` in here —
 * rather than in the always-mounted slideout wrapper — keeps the schema, object-
 * type, and association-type fetches it drives off every settings-page visit and
 * on the actual create flow only.
 *
 * The mint mutation deliberately lives in the always-mounted parent, NOT here: if
 * the slideout closes mid-flight this form unmounts, and a mutation owned here
 * would drop its `onSuccess`/`onSettled` — losing the one-time secret display and
 * the list refresh.
 */
export function ApiAccessCreateForm({
  orgId,
  formId,
  onSubmit,
  isSubmitting,
  onCanSubmitChange,
  onDirtyChange,
  onNameChange,
  onGrantsChange,
}: ApiAccessCreateFormProps) {
  const { user } = useAuthInfo();
  // `user` is null in logged-out / expired-session states. Resolve the id once,
  // safely, so neither the mint params below nor this callback's dep array can
  // dereference null and crash the slideout.
  const userId: number | null = user?.id ?? null;

  // Pre-filled so the slideout opens ready to submit — with every type selected
  // by default (see ApiAccessGrantPicker), creating a credential is one click.
  // Editable, and not enforced unique, so a second credential keeps this name
  // until renamed.
  const [name, setName] = useState(DEFAULT_CREDENTIAL_NAME);
  const [grants, setGrants] = useState<GrantSelection[]>([]);

  // Name-only dirty signal: a deliberate compromise. The auto-selecting
  // ApiAccessGrantPicker has no capturable baseline (every type starts ticked
  // with no prior "chosen" state to diff against), so a grant-toggle change
  // while the default name is kept is a known, accepted gap. The meaningful
  // loss this guards against — a user-typed custom name — IS covered.
  const isDirty = name.trim() !== DEFAULT_CREDENTIAL_NAME;
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  // Reports every name change up to the wrapper — see onNameChange's doc
  // comment on why: a retry-reuse entity id must be dropped the moment the
  // user is naming a different credential.
  useEffect(() => {
    onNameChange?.(name);
  }, [name, onNameChange]);

  // Reports every grant-selection change up to the wrapper — see
  // onGrantsChange's doc comment on why: a changed selection must always drop
  // the retry-reuse id and mint a fresh entity instead of silently reusing one
  // created with the OLD selection.
  useEffect(() => {
    onGrantsChange?.(grants);
  }, [grants, onGrantsChange]);

  // Owned here because both consumers need the same view: the picker renders its
  // per-type Public/Private control from it, and the submit below passes the
  // selections straight through to the service-account mint.
  const scopings = useGrantScopings(orgId);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
  }, []);

  const handleGrantsChange = useCallback((next: GrantSelection[]) => {
    setGrants(next);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmedName = name.trim();
    if (userId === null) {
      // No authenticated user (logged out / expired session): the mint would
      // fail server-side, so surface the same error the mint path shows rather
      // than dereferencing a null user. Submit is disabled in this state too.
      toast.error(
        formatGrantBuildError(undefined, 'create', trimmedName).toastMessage
      );
      return;
    }
    const params: MintCredentialParams = {
      orgId,
      userId,
      displayName: trimmedName,
      selections: grants,
    };
    onSubmit(params);
  }, [grants, orgId, userId, name, onSubmit]);

  const isSubmitDisabled =
    !name.trim() || grants.length === 0 || isSubmitting || userId === null;

  // The footer button (outside this subtree) mirrors this; reset on unmount so
  // a reopened slideout never starts with a stale "enabled".
  useEffect(() => {
    onCanSubmitChange?.(!isSubmitDisabled);
    return () => onCanSubmitChange?.(false);
  }, [isSubmitDisabled, onCanSubmitChange]);

  // Enter in the name field and the footer's `form=`-targeted submit button
  // both arrive here; the guard mirrors the button's disabled state.
  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isSubmitDisabled) {
        return;
      }
      handleSubmit();
    },
    [handleSubmit, isSubmitDisabled]
  );

  return (
    <form
      id={formId}
      onSubmit={handleFormSubmit}
      className='flex flex-col gap-5 pb-2'
    >
      <Input
        label='Name'
        value={name}
        onChange={handleNameChange}
        isRequired
        size='md'
        hint='Name it after the integration that will use it, for example Snowflake connector.'
      />
      <ApiAccessGrantPicker
        value={grants}
        onChange={handleGrantsChange}
        scopings={scopings}
        showWriteColumn
      />
    </form>
  );
}
